import { useForm } from "@tanstack/react-form";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  getRouteApi,
  notFound,
  useBlocker,
} from "@tanstack/react-router";
import { FileText, X } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Card, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { FileUpload } from "~/components/ui/file-upload";
import { FormField } from "~/components/ui/form-field";
import { PageHeader } from "~/components/ui/page-title";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  graduateListSetupQueryOptions,
  previewGraduateListFn,
} from "~/lib/functions/graduate-list";
import {
  GRADUATE_LIST_COPY,
  GRADUATE_LIST_MAX_BYTES,
  type GraduateListOutcome,
  type GraduateListPreview,
  type GraduateRow,
} from "~/lib/shared/graduate-list";
import { parseGraduateList } from "~/lib/shared/graduate-list-parse";
import { UserFacingError } from "~/lib/shared/user-facing-error";

import { OrganizationMark } from "../-organization-mark";

import { GraduateReview } from "./-graduate-review";

const organizationRoute = getRouteApi("/_user/my-organization/$slug");

export const Route = createFileRoute("/_user/my-organization/$slug/graduates")({
  staticData: { title: "Graduate list" },
  loader: async ({ context, params }) => {
    const setup = await context.queryClient.ensureQueryData(
      graduateListSetupQueryOptions(params.slug),
    );
    // Closed while the switch is off, or for someone who doesn't represent
    // the organization.
    if (!setup) throw notFound();
  },
  component: GraduateListPage,
});

const MEMBERSHIP = "membership";

type Stage =
  | { kind: "upload" }
  | {
      kind: "review";
      rows: GraduateRow[];
      preview: GraduateListPreview;
    }
  | { kind: "done"; outcome: GraduateListOutcome };

function errorText(error: unknown): string {
  return error instanceof UserFacingError
    ? error.message
    : "Something went wrong. Please try again.";
}

function GraduateListPage(): React.JSX.Element {
  const { organization } = organizationRoute.useLoaderData();
  const [stage, setStage] = useState<Stage>({ kind: "upload" });

  // Leaving with matches on screen discards the list; say so first, for a
  // link in the app and for closing or reloading the tab.
  const leave = useBlocker({
    shouldBlockFn: () => true,
    disabled: !(stage.kind === "review" && stage.preview.matches.length > 0),
    withResolver: true,
  });

  return (
    <div className="space-y-8">
      <Dialog
        open={leave.status === "blocked"}
        onOpenChange={(open) => !open && leave.reset?.()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Leave without confirming?</DialogTitle>
            <DialogDescription>
              {GRADUATE_LIST_COPY.leaveWarning}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => leave.reset?.()}>
              Stay
            </Button>
            <Button variant="primary" onClick={() => leave.proceed?.()}>
              Leave and discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-start gap-4">
        <OrganizationMark
          name={organization.name}
          logoUrl={organization.logoUrl}
        />
        <PageHeader
          className="flex-1"
          eyebrow={organization.name}
          title="Confirm graduates from a list"
          description={GRADUATE_LIST_COPY.uploadIntro(organization.name)}
        />
      </div>

      {stage.kind === "upload" && (
        <UploadForm
          onChecked={(rows, preview) =>
            setStage({ kind: "review", rows, preview })
          }
        />
      )}

      {stage.kind === "review" && stage.preview.matches.length === 0 && (
        <Card className="max-w-xl">
          <CardTitle>No matches with public coach profiles</CardTitle>
          <p className="text-text-secondary text-sm">
            {GRADUATE_LIST_COPY.noMatches(stage.preview.checked)}
          </p>
          <Button onClick={() => setStage({ kind: "upload" })}>
            Upload another list
          </Button>
        </Card>
      )}

      {stage.kind === "review" && stage.preview.matches.length > 0 && (
        <GraduateReview
          slug={organization.slug}
          organization={organization.name}
          rows={stage.rows}
          preview={stage.preview}
          onDone={(outcome) => setStage({ kind: "done", outcome })}
          onRestart={() => setStage({ kind: "upload" })}
        />
      )}

      {stage.kind === "done" && (
        <Card className="max-w-xl">
          <CardTitle>{GRADUATE_LIST_COPY.done(stage.outcome)}</CardTitle>
          <p className="text-text-secondary text-sm">
            {stage.outcome.confirmed > 0 &&
              `Their profiles now show "Verified by ${organization.name}". `}
            {GRADUATE_LIST_COPY.doneNotice}
          </p>
          {stage.outcome.skipped > 0 && (
            <p className="text-text-tertiary text-sm">
              {stage.outcome.skipped}{" "}
              {stage.outcome.skipped === 1 ? "match" : "matches"} changed since
              you checked the list and{" "}
              {stage.outcome.skipped === 1 ? "was" : "were"} left as{" "}
              {stage.outcome.skipped === 1 ? "it was" : "they were"}.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setStage({ kind: "upload" })}>
              Check another list
            </Button>
            <Link
              to="/my-organization/$slug/claims"
              params={{ slug: organization.slug }}
              className="inline-flex h-10 items-center px-4 text-sm font-medium text-purple-700 hover:underline"
            >
              View coach claims
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}

function UploadForm({
  onChecked,
}: {
  onChecked: (rows: GraduateRow[], preview: GraduateListPreview) => void;
}): React.JSX.Element {
  const { slug } = Route.useParams();
  const { organization } = organizationRoute.useLoaderData();
  const { data: setup } = useSuspenseQuery(graduateListSetupQueryOptions(slug));
  const programs = setup?.programs ?? [];
  const [problem, setProblem] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      target: programs[0]?.id ?? MEMBERSHIP,
      file: null as File | null,
      pasted: "",
      statement: false,
    },
    onSubmit: async ({ value }) => {
      setProblem(null);
      const parsed = parseGraduateList(
        value.file ? await value.file.text() : value.pasted,
        {
          programs,
          defaultProgramId: value.target === MEMBERSHIP ? null : value.target,
          fileName: value.file?.name,
        },
      );
      if (!parsed.ok) {
        setProblem(parsed.error);
        return;
      }
      try {
        const preview = await previewGraduateListFn({
          data: { slug, rows: parsed.rows },
        });
        onChecked(parsed.rows, preview);
      } catch (caught) {
        setProblem(errorText(caught));
      }
    },
  });

  return (
    <Card className="max-w-2xl">
      <ul className="text-text-secondary list-disc space-y-1 pl-5 text-sm">
        {GRADUATE_LIST_COPY.uploadDetails.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <form
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.Field name="target">
          {(field) => (
            <FormField
              label="Program"
              inputId="graduate-list-target"
              helpText="Rows without a program column use this one."
            >
              <Select
                value={field.state.value}
                onValueChange={field.handleChange}
              >
                <SelectTrigger
                  id="graduate-list-target"
                  className="w-full justify-between"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={program.id}>
                      {program.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={MEMBERSHIP}>
                    Membership of {organization.name}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          )}
        </form.Field>

        <form.Field name="file">
          {(field) => (
            <FormField
              label="Spreadsheet (.csv)"
              helpText="Optional columns: program and certification date. Save an Excel or Google Sheets file as CSV first."
            >
              {field.state.value ? (
                <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-4 text-sm">
                  <FileText className="size-4 text-purple-700" />
                  <span className="min-w-0 flex-1 truncate">
                    {field.state.value.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => field.handleChange(null)}
                  >
                    <X className="size-4" />
                    Remove
                  </Button>
                </div>
              ) : (
                <FileUpload
                  accept=".csv,text/csv"
                  maxSize={GRADUATE_LIST_MAX_BYTES}
                  label="Drop a CSV file here, or click to choose one"
                  onError={setProblem}
                  onChange={([file]) => {
                    if (!file) return;
                    if (!/\.(csv|txt)$/i.test(file.name)) {
                      setProblem(
                        "We can only read CSV files. Save the spreadsheet as CSV and choose that file, or paste the emails instead.",
                      );
                      return;
                    }
                    setProblem(null);
                    field.handleChange(file);
                  }}
                />
              )}
            </FormField>
          )}
        </form.Field>

        <form.Subscribe selector={(state) => state.values.file}>
          {(file) => (
            <form.Field name="pasted">
              {(field) => (
                <FormField
                  label="Or paste emails, one per line"
                  inputId="graduate-list-paste"
                >
                  <Textarea
                    id="graduate-list-paste"
                    rows={5}
                    disabled={file !== null}
                    placeholder={"sam.okafor@example.com\nlena.f@example.com"}
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                </FormField>
              )}
            </form.Field>
          )}
        </form.Subscribe>

        <form.Field name="statement">
          {(field) => (
            <label className="flex items-start gap-3 text-sm text-gray-900">
              <Checkbox
                className="mt-0.5"
                checked={field.state.value}
                onCheckedChange={(checked) =>
                  field.handleChange(checked === true)
                }
              />
              <span>{GRADUATE_LIST_COPY.statement(organization.name)}</span>
            </label>
          )}
        </form.Field>

        {problem && (
          <p
            role="alert"
            className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          >
            {problem}
          </p>
        )}

        <form.Subscribe
          selector={(state) =>
            [
              state.values.statement &&
                (state.values.file !== null ||
                  state.values.pasted.trim() !== ""),
              state.isSubmitting,
            ] as const
          }
        >
          {([ready, submitting]) => (
            <Button
              type="submit"
              variant="primary"
              className="w-full sm:w-auto"
              disabled={!ready || submitting}
            >
              {submitting ? "Checking…" : "Find matches"}
            </Button>
          )}
        </form.Subscribe>
      </form>
    </Card>
  );
}
